import { api, unwrap } from '../../shared/api/client';
import type { components } from '../../shared/api/schema.gen';

type S = components['schemas'];

export type TRetentionUsage = S['RetentionUsageResDto'];
export type TRetentionVolume = S['RetentionVolumeResDto'];
export type TRetentionSettings = S['RetentionSettingsResDto'];
export type TStorageCleanup = S['StorageCleanupResDto'];
export type TStorageLevels = S['StorageLevelsResDto'];
export type TFolderLevel = S['FolderLevelDto'];
export type TFlowLevel = S['FlowLevelDto'];
export type TFlowLevelList = S['FlowLevelListResDto'];
export type TRetentionKeeps = S['RetentionKeepsDto'];
export type TRetryWarning = S['RetryWarningDto'];
export type TRetentionImpact = S['RetentionImpactResDto'];
export type TImpactRequest = S['RetentionImpactReqDto'];
export type TDeletePreview = S['DeletePreviewResDto'];
export type TDeleteRequest = S['ClearNowReqDto'];
export type TRetentionPeriod = TRetentionSettings['period'];

export const retentionKeys = {
  all: ['retention'] as const,
  usage: ['retention', 'usage'] as const,
  levels: ['retention', 'levels'] as const,
  flows: (q: string, limit: number) => ['retention', 'flows', q, limit] as const,
  empty: ['retention', 'flows', 'empty'] as const,
  exceptions: (q: string, offset: number) => ['retention', 'exceptions', q, offset] as const,
};

export const retentionUsage = (): Promise<TRetentionUsage> =>
  unwrap(api.GET('/v1/retention/usage'));

export const storageLevels = (): Promise<TStorageLevels> => unwrap(api.GET('/v1/retention/levels'));

export const storageFlows = (query: {
  q?: string;
  limit: number;
  empty?: boolean;
}): Promise<TFlowLevelList> =>
  unwrap(
    api.GET('/v1/retention/flows', {
      params: {
        query: {
          limit: query.limit,
          ...(query.q ? { q: query.q } : {}),
          ...(query.empty ? { empty: 'true' as const } : {}),
        },
      },
    }),
  );

export const retentionExceptions = (query: {
  q?: string;
  offset: number;
  limit: number;
}): Promise<TFlowLevelList> =>
  unwrap(
    api.GET('/v1/retention/exceptions', {
      params: {
        query: { limit: query.limit, offset: query.offset, ...(query.q ? { q: query.q } : {}) },
      },
    }),
  );

export const retentionImpact = (body: TImpactRequest): Promise<TRetentionImpact> =>
  unwrap(api.POST('/v1/retention/impact', { body }));

export const setDefaultPeriod = (
  period: TRetentionPeriod,
  keepFlowIds: string[] = [],
): Promise<TRetentionSettings> =>
  unwrap(api.PATCH('/v1/retention/settings', { body: { period, keepFlowIds } }));

export const setFolderPeriod = (
  folderId: string,
  period: TRetentionPeriod | null,
  keepFlowIds: string[] = [],
): Promise<TStorageLevels> =>
  unwrap(
    api.PUT('/v1/retention/folders/{folderId}', {
      params: { path: { folderId } },
      body: { period, keepFlowIds },
    }),
  );

export const setFlowPeriod = (flowId: string, retentionPeriod: TRetentionPeriod | null) =>
  unwrap(
    api.PATCH('/v1/flows/{id}', { params: { path: { id: flowId } }, body: { retentionPeriod } }),
  );

export const setStorageQuota = (quotaBytes: number): Promise<TRetentionSettings> =>
  unwrap(api.PATCH('/v1/retention/quota', { body: { quotaBytes } }));

export const deletePreview = (body: {
  scope: TDeleteRequest['scope'];
  olderThanDays: number;
}): Promise<TDeletePreview> => unwrap(api.POST('/v1/retention/preview', { body }));

export const deleteNow = (body: TDeleteRequest) =>
  unwrap(api.POST('/v1/retention/clear', { body }));

export const retryCleanup = () => unwrap(api.POST('/v1/retention/run'));
